import React from 'react';
import NoRecords from '../../shared/NoRecords';
import { SERIES } from '../lib/colors';
import { fmtCount, fmtDate } from '../lib/format';

/**
 * The results ladder (spec Part F) — the page's one face.
 *
 * A vertical ladder read top to bottom as a Google results page: four rungs in the
 * physical order `1-3 · 4-10 · 11-20 · 21+`, each rung's bar weighted by the band's
 * `queries` (distinct searches across the page window), scaled to the WIDEST value on
 * the ladder so the shape is readable. A per-rung sparkline from `trend` turns the
 * snapshot into a trajectory, and a dashed marker shows where the band sat in the same
 * window a year earlier when — and only when — the backend holds that year.
 *
 * ── Props, not self-fetch ───────────────────────────────────────────────────────
 * A standalone Tier-3 panel fed by the envelope's `position_bands` panel:
 *   facts.summary { total_queries, top_three, top_three_share, first_page,
 *                   prior_year_available, prior_year_start, prior_year_end,
 *                   prior_year_days_with_data }
 *   facts.items[] { band, queries, prior_year_queries, impressions, clicks, share,
 *                   drill }                                                   (always 4)
 *   facts.trend[] { month, band, queries }                                    (sparkline only)
 *
 * ── Year-ago marker ─────────────────────────────────────────────────────────────
 * `items[].prior_year_queries` counts distinct searches in the same window a year
 * earlier, measured the same way as `queries`, so the two share a scale. `trend` rows
 * count searches per calendar month and cannot share it, so the marker never reads
 * `trend`. Bars and markers divide by one scale — the largest of every `queries` and,
 * when markers are shown, every `prior_year_queries` — because a year-ago value can
 * exceed every current band. The marker is hidden when the summary says the year is
 * unavailable or only partly covered, and per band when its value is null.
 *
 * ── Render-only (§2.4.8) ────────────────────────────────────────────────────────
 * Every number arrives from the backend; the only arithmetic is the bar and marker
 * position. Nothing here branches on which property is loaded.
 *
 * ── Degenerate cases (Part F) ───────────────────────────────────────────────────
 * Zero searches arrive as `confidence: "insufficient"` and never reach this
 * component — the page routes those to `GenericCard` (card + sentence, no ladder).
 * A single populated band still renders all four rungs, three at zero: the missing
 * weight at the top IS the finding, so an empty rung is never collapsed, and it is
 * not clickable because its drill has no records behind it.
 */

const BAND_ORDER = ['1-3', '4-10', '11-20', '21+'];

// The bullet beside each rung label. One glyph for all four rungs: the bar length and
// the band's place on the results page already say which rung matters, and a bullet that
// changes shape per row reads as four different kinds of row rather than as a ranking.
const RUNG_MARK = '●';

// What each band means as a place on the Google results page (~10 results per page).
// These turn the four rows from a bar chart of counts into a picture of a results page
// read top to bottom — the whole point of the ladder (Part F).
const BAND_MEANING = {
  '1-3': 'Top of page 1',
  '4-10': 'Rest of page 1',
  '11-20': 'Page 2',
  '21+': 'Page 3 and beyond',
};

const MS_PER_DAY = 86400000;

/**
 * Whole days in the inclusive range `start..end` (both `YYYY-MM-DD`), or null when
 * either end is not a calendar date. Both ends are parsed as UTC midnights, never with
 * `new Date(string)` in local time, so a DST change cannot shift the count.
 */
const inclusiveDays = (start, end) => {
  const parse = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
  };
  const from = parse(start);
  const to = parse(end);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / MS_PER_DAY) + 1;
};

/**
 * Whether the year-ago markers may be drawn: the summary says the year is available
 * AND the backend holds every day of it. `prior_year_available` can be true for a year
 * held only in part, and a marker over a part-covered window reads as a decline that
 * never happened — so when `prior_year_days_with_data` is present it must reach the
 * inclusive length of `prior_year_start..prior_year_end`. A summary without
 * `prior_year_days_with_data` is trusted on `prior_year_available` alone; one that has a
 * day count but dates that don't parse can't be checked, so the marker is hidden.
 *
 * @param {object} [summary] `position_bands.facts.summary`
 * @returns {boolean}
 */
const priorYearShown = (summary) => {
  if (summary?.prior_year_available !== true) return false;
  const held = summary.prior_year_days_with_data;
  if (held == null) return true;
  const span = inclusiveDays(summary.prior_year_start, summary.prior_year_end);
  return span != null && Number(held) >= span;
};

/**
 * One band's year-ago `queries` for the marker, or null when the marker is not shown or
 * the band carries no value. A null is never coerced to zero.
 */
const markerValue = (item, shown) => {
  if (!shown || item?.prior_year_queries == null) return null;
  const value = Number(item.prior_year_queries);
  return Number.isFinite(value) ? value : null;
};

/** A minimal inline sparkline of a band's monthly queries. */
const RungSparkline = ({ series }) => {
  const points = Array.isArray(series) ? series : [];
  if (points.length < 2) return <div className="w-16 h-6" aria-hidden="true" />;
  const values = points.map((p) => Number(p.queries) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const w = 64;
  const h = 24;
  const step = w / (values.length - 1);
  const d = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg className="w-16 h-6 shrink-0" viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden="true" preserveAspectRatio="none">
      <path d={d} stroke={SERIES.clicks} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const Rung = ({ band, item, trend, scaleMax, marker, onDrill }) => {
  const queries = Number(item?.queries) || 0;
  const width = scaleMax > 0 ? `${(queries / scaleMax) * 100}%` : '0%';

  // Sorted ascending by month for the sparkline only; the year-ago marker never reads it.
  const bandTrend = trend
    .filter((r) => r.band === band)
    .sort((a, b) => String(a.month).localeCompare(String(b.month)));
  const markerLeft = marker != null && scaleMax > 0 ? `${(marker / scaleMax) * 100}%` : null;

  // An empty rung has no records behind its drill, so it is not a button.
  const drillable = queries > 0 && Boolean(item?.drill) && typeof onDrill === 'function';

  const bar = (
    <div className="relative flex-1 min-w-0">
      <div className="h-6 w-full rounded bg-gray-100 dark:bg-gray-800" aria-hidden="true">
        <div data-testid={`rung-bar-${band}`} className="h-full rounded" style={{ width, backgroundColor: SERIES.clicks }} />
      </div>
      {/* Year-ago marker — this band's `prior_year_queries` on the same scale as the bar
          (dashed), only when the backend holds that year. */}
      {markerLeft && (
        <div
          data-testid={`ghost-${band}`}
          className="absolute top-0 bottom-0 border-l-2 border-dashed border-gray-500 dark:border-gray-400"
          style={{ left: markerLeft }}
          aria-hidden="true"
        />
      )}
    </div>
  );

  const label = (
    <span className="w-40 shrink-0">
      <span className="block tabular-nums text-sm font-semibold text-gray-800 dark:text-gray-200">
        <span className="mr-1.5 text-gray-400 dark:text-gray-500" aria-hidden="true">{RUNG_MARK}</span>
        {band}
      </span>
      <span className="block text-xs text-gray-500 dark:text-gray-400">{BAND_MEANING[band]}</span>
    </span>
  );

  const count = (
    <span className="w-20 shrink-0 text-right tabular-nums text-sm text-gray-800 dark:text-gray-200">
      {fmtCount(queries)}
    </span>
  );

  const rowInner = (
    <>
      {label}
      {bar}
      <RungSparkline series={bandTrend} />
      {count}
      {drillable && (
        <svg className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
      )}
    </>
  );

  if (drillable) {
    return (
      <li>
        <button
          type="button"
          data-band={band}
          onClick={() => onDrill(item.drill)}
          className="w-full flex items-center gap-3 py-2 px-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset transition-colors"
        >
          {rowInner}
        </button>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-3 py-2 px-2" data-band={band}>
      {rowInner}
    </li>
  );
};

const PositionBands = ({ facts, onDrill }) => {
  const items = Array.isArray(facts?.items) ? facts.items : [];
  const trend = Array.isArray(facts?.trend) ? facts.trend : [];

  if (items.length === 0) {
    return <NoRecords />;
  }

  const summary = facts?.summary;
  const byBand = new Map(items.map((it) => [it.band, it]));
  const shown = priorYearShown(summary);
  const markers = new Map(BAND_ORDER.map((band) => [band, markerValue(byBand.get(band), shown)]));

  // One scale for bars and markers: a year-ago value can exceed every current band.
  const scaleMax = Math.max(
    0,
    ...items.map((it) => Number(it.queries) || 0),
    ...items.map((it) => markerValue(it, shown) ?? 0),
  );

  // Whether ANY rung will actually draw a marker, so the caption never promises one the
  // reader will not see.
  const hasGhostMarker = scaleMax > 0 && BAND_ORDER.some((band) => markers.get(band) != null);
  const yearAgoRange = typeof summary?.prior_year_start === 'string' && typeof summary?.prior_year_end === 'string'
    ? ` (${fmtDate(summary.prior_year_start)} – ${fmtDate(summary.prior_year_end)})`
    : '';

  return (
    <div className="min-h-[240px] py-2" data-testid="position-bands">
      {/* No front-end-composed caption here: PanelCard renders the backend `sentence`
          directly above this (render-only — the backend sentence is the product). */}
      {/* A left rail the rungs hang off, so the four read as one results page scanned
          top to bottom rather than four separate bars. */}
      <ul className="space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
        {BAND_ORDER.map((band) => (
          <Rung
            key={band}
            band={band}
            item={byBand.get(band)}
            trend={trend}
            scaleMax={scaleMax}
            marker={markers.get(band)}
            onDrill={onDrill}
          />
        ))}
      </ul>
      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        Bands are Google result positions, read top to bottom like a results page — the top
        rung is the first results people see.
        {hasGhostMarker ? ` Dashed marker: the same window a year earlier${yearAgoRange}.` : ''}
      </p>
    </div>
  );
};

export default PositionBands;
